import type {
  IProGroupsNavigationStateStorage,
  PersistedNavigationState,
  ProGroupsSectionKey,
} from '../../services/progroups';
import { resolveAuthContext } from '../../services/auth/authContext';

type PersistedSection = ProGroupsSectionKey;

interface PersistedNavigationStateInternal extends PersistedNavigationState {
  subsectionBySection: Record<PersistedSection, string>;
}

interface PersistedUiState {
  version: 1;
  navigation: PersistedNavigationStateInternal;
  tabs: Record<string, unknown>;
  updatedAt: string;
}

const STORAGE_PREFIX = 'pro-groups-ui-state-v1';

const DEFAULT_SUBSECTION_BY_SECTION: Record<PersistedSection, string> = {
  Debug: 'Tools',
  Clients: 'Add Client',
  Stats: 'Usage',
  Account: 'Account Settings',
};

const EMPTY_STATE: PersistedUiState = {
  version: 1,
  navigation: {
    activeSection: 'Clients',
    subsectionBySection: DEFAULT_SUBSECTION_BY_SECTION,
  },
  tabs: {},
  updatedAt: new Date(0).toISOString(),
};

export function resolveProGroupsUiStateUserKey(): string {
  const { userStore: globalStore } = resolveAuthContext();

  const state = globalStore?.getState ? globalStore.getState() : null;
  const user = state?.session?.user;
  return user?.userId || user?.email || 'anonymous';
}

export class ProGroupsUiStateStorage implements IProGroupsNavigationStateStorage {
  private storage: any;
  private storageKey: string;

  constructor(storage: any, userKey?: string) {
    this.storage = storage;
    this.storageKey = `${STORAGE_PREFIX}:${userKey || resolveProGroupsUiStateUserKey()}`;
    console.log('🗄️ ProGroupsUiStateStorage: Initialized with key:', this.storageKey);
  }

  async loadNavigation(): Promise<PersistedNavigationStateInternal | null> {
    const state = await this.readState();
    return state?.navigation ?? null;
  }

  async saveNavigation(navigation: PersistedNavigationState): Promise<void> {
    await this.patchState((state) => ({
      ...state,
      navigation: navigation as PersistedNavigationStateInternal,
      updatedAt: new Date().toISOString(),
    }));
  }

  async loadTabState<T>(tabKey: string): Promise<T | null> {
    const state = await this.readState();
    const result = state ? (state.tabs[tabKey] as T | undefined) ?? null : null;
    console.log(`🗄️ ProGroupsUiStateStorage: Loading tab '${tabKey}' from key '${this.storageKey}':`, result ? 'CACHED DATA FOUND' : 'no cache');
    return result;
  }

  async saveTabState<T>(tabKey: string, value: T): Promise<void> {
    await this.patchState((state) => ({
      ...state,
      tabs: {
        ...state.tabs,
        [tabKey]: value,
      },
      updatedAt: new Date().toISOString(),
    }));
  }

  async clearAllTabs(): Promise<void> {
    await this.patchState((state) => ({
      ...state,
      tabs: {},
      updatedAt: new Date().toISOString(),
    }));
    console.log('🗄️ ProGroupsUiStateStorage: Cleared all tab cache from key:', this.storageKey);
  }

  private async readState(): Promise<PersistedUiState | null> {
    if (!this.storage?.getItem) {
      return null;
    }

    try {
      const raw = await this.storage.getItem(this.storageKey);
      if (!raw) {
        return null;
      }

      const parsed = JSON.parse(raw) as PersistedUiState;
      if (!parsed || parsed.version !== 1 || typeof parsed !== 'object') {
        return null;
      }

      if (!parsed.navigation || !parsed.tabs) {
        return null;
      }

      return parsed;
    } catch (error) {
      console.warn('ProGroupsUiStateStorage: failed to read state', error);
      return null;
    }
  }

  private async patchState(
    updater: (state: PersistedUiState) => PersistedUiState
  ): Promise<void> {
    if (!this.storage?.setItem) {
      return;
    }

    const current = (await this.readState()) ?? EMPTY_STATE;
    const next = updater(current);

    try {
      await this.storage.setItem(this.storageKey, JSON.stringify(next));
    } catch (error) {
      console.warn('ProGroupsUiStateStorage: failed to write state', error);
    }
  }
}
